import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import { ReactElement, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Alert,
    Box,
    Button,
    DatePicker,
    Grid,
    Snackbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@methanesat/ui-components';

import { MiniLegend } from '../../../components/EmissionsMapLegend';
import { EmissionsPageMap } from '../../../components/EmissionsPageMap';
import { EmissionsPageMapDrawers } from '../../../components/EmissionsPageMap/EmissionsPageMapContent/EmissionsPageMapDrawers';
import {
    handleTimeSelectorDayClick,
    stacDatesToTimeSelector
} from '../../../components/EmissionsPageMap/LayerDataDrawer/DataSpecific/EmissionsDrawers/helpers';
import {
    emissionsInfoToCardProps,
    fillUnavailableEmissionCards
} from '../../../components/EmissionsPageMap/LayerDataDrawer/DrawerWithCards';
import { EmissionsPageZoomControls } from '../../../components/EmissionsPageZoomControls';
import { FloatingTargetSummary } from '../../../components/FloatingTargetSummary';
import Head from '../../../components/Head';
import Header from '../../../components/Header';
import { IntroTooltip } from '../../../components/IntroDrawer';
import { MapToggleButton } from '../../../components/MapToggleButton';
import { PageContainer } from '../../../components/PageContainer';
import {
    getGlobalDateOptions,
    getTimeSelectorTimespan,
    GoogleBasemaps,
    maxZoomBaseMap,
    minZoomBaseMap,
    MOBILE_DRAWER_PREVIEW_HEIGHT
} from '../../../consts';
import { checkEmissionsMapEnv, ONE_MSAT_DATE_RANGE, SHOW_VIEW_ON_DESKTOP_ALERT } from '../../../environmentVariables';
import {
    useAppSelector,
    useGetCapturesWithinGlobalTimeRange,
    useGetCapturesWithinViewport,
    useGetHistoricalSamplesForTarget,
    useGetSingleCaptureWithinViewport,
    useSelectedFeaturePickInfos,
    useTranslate
} from '../../../hooks';
import { buildEmissionDrawerDateChangeState, getArrayURLParams } from '../../../hooks/data/emissionsDrawerSelection';
import {
    useDrawers,
    useOpenDrawer,
    useCloseDrawer,
    useSetHeightBottomDrawer,
    useShouldCloseDrawers
} from '../../../hooks/pages/emissions-map';
import {
    addOneMapFeatureHighlight,
    clearActiveCapture,
    drawerStateReducer,
    initialDrawerState,
    removeAllMapFeatureHighlights,
    selectActiveCapture,
    selectEmissionsDrawerCardData,
    selectEmissionsMapLayerConfigs,
    selectEmissionsMapName,
    selectFlooredZoom,
    selectGlobalDate,
    selectHighlightedFeature,
    selectHighlightedLayerId,
    selectIsAnyChipExpanded,
    selectMethaneProduct,
    selectPlatform,
    setActiveCapture,
    setEmissionsDrawerCardData,
    setFetchingDrawerInfo,
    setEmissionsMapName,
    setGlobalDate,
    setSelectedFeatureParams,
    setTargetDate,
    toggleAllChips,
    selectUrlParamsInitialized
} from '../../../reducers';
import type { RootState } from '../../../store';
import {
    CaptureFeatureProperties,
    CapturePlusCount,
    EmissionDrawerCardData,
    GoogleBasemapNames,
    isBasinInfo,
    isClusteredPointInfrastructureInfo,
    isOGIInfo,
    isPipelineInfo,
    isPlumeEmissionInfo,
    isPointInfrastructureInfo,
    isRasterInfo,
    isTargetInfo,
    MethaneLayerIds,
    OGIInfo,
    PageIds,
    PickInfoToPrioritize,
    Platforms,
    StacDateBlock,
    SnapPointIds,
    SupportedLocaleObject,
    TargetInfo
} from '../../../types';

import {
    addLanguageAndDictionaryToProps,
    addLanguageToPathParams,
    analytics,
    createHighlightPayload,
    ErrorBoundary,
    formatDateForUrl,
    formatEmissions,
    getActiveCaptureParams,
    getEmissionSelectionParams,
    getFromLocalStorage,
    getStacCaptureEmissions,
    handleErrorBoundaryError,
    setInLocalStorage,
    makeUseOnMapClick,
    checkForSingleCard,
    getDayEnd
} from '../../../utils';

const getNextActiveEmissionCardIndex = (cards: EmissionDrawerCardData[], preferredTargetId: string | null) => {
    const preferredCardIndex = cards.findIndex((card) => card.targetId === preferredTargetId && !card.dataUnavailable);

    return preferredCardIndex !== -1 ? preferredCardIndex : 0;
};

/**
 * Emissions map gives context to where methane plumes are, when they were detected, how much is being emitted,
 * and who might be emitting them.
 */
function EmissionsMap(): ReactElement {
    const t = useTranslate();
    const dispatch = useDispatch();
    const VIEW_ON_DESKTOP_BANNER = 'viewOnDesktopBanner';

    const [drawerState, drawerDispatch] = useReducer(drawerStateReducer, initialDrawerState);
    const helpButtonRef = useRef<HTMLButtonElement>(null);

    // mobile banner state
    const [isBannerOpen, setIsBannerOpen] = useState(true);

    useEffect(() => {
        const desktopBannerDismissed = getFromLocalStorage(VIEW_ON_DESKTOP_BANNER);
        // close banner if user previously dismissed the banner
        if (desktopBannerDismissed === true) setIsBannerOpen(false);
    }, []);

    const handleBannerClose = () => {
        setIsBannerOpen(false);
        setInLocalStorage(VIEW_ON_DESKTOP_BANNER, true);
    };
    const [drawerWidth, setDrawerWidth] = useState(0);

    /** Data for the accordions in drawer with cards */
    const [drawerCardData, setDrawerCardData] = useState<(EmissionDrawerCardData | OGIInfo | TargetInfo)[] | null>(
        null
    );

    /** Tracks the in-flight drawer date change until refreshed cards are rebuilt.  */
    const pendingDateChangeTargetId = useRef<string | null>(null);
    /** Tracks the exact capture datetime of the clicked day. */
    const pendingDateChangeDayDate = useRef<Date | null>(null);

    // send drawer card data to redux for use in the URL
    useDrawers({
        drawerCardData,
        dispatch
    });

    const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);
    const [activeCardIndex, setActiveCardIndex] = useState<number>(0);

    const handleAccordionChange = (newExpandedAccordion: string, cardIndex: number) => {
        setExpandedAccordion(newExpandedAccordion === expandedAccordion ? null : newExpandedAccordion);
        setActiveCardIndex(cardIndex);
    };

    const allDrawersClosed = useMemo(() => {
        return (
            Object.entries(drawerState)
                // Exclude `bottomDrawerSnapPoint`, which tracks the mobile drawer position (not open/closed state)
                .filter(([key]) => key !== 'bottomDrawerSnapPoint')
                .every(([_, currentDrawerOpen]) => !currentDrawerOpen)
        );
    }, [drawerState]);

    const isMapInteractive = useSelector(
        (state: RootState) => state.pages.emissions.mapInterface.gestureHandling !== 'none'
    );
    const isInitialized = useSelector(selectUrlParamsInitialized);

    const mapName = useSelector(selectEmissionsMapName);
    const globalDate = useSelector(selectGlobalDate);
    const emissionsDrawerSelection = useSelector(selectEmissionsDrawerCardData);
    const fetchingDrawerInfo = useSelector(
        (state: RootState) => state.pages.emissions.selectedFeature.fetchingDrawerInfo
    );
    const isAnyChipExpanded = useSelector((state: RootState) => selectIsAnyChipExpanded(state));

    // intro halo tooltips
    const isTooltipVisible = useAppSelector((state) => state.tooltip.visible);

    const areaEmissionLayerEnabled = useSelector(
        (state: RootState) => selectEmissionsMapLayerConfigs(state)[MethaneLayerIds.areaEmissionRaster].enabled
    );
    const plumeEmissionLayerEnabled = useSelector(
        (state: RootState) => selectEmissionsMapLayerConfigs(state)[MethaneLayerIds.plumeEmissionRate].enabled
    );

    // highlighted infrastructure object
    const highlightedFeature = useSelector(selectHighlightedFeature);

    const platform = useSelector(selectPlatform);
    const product = useSelector(selectMethaneProduct);

    const GLOBAL_DATE_OPTIONS = getGlobalDateOptions(platform);

    const { featureList, isFetching } = useGetCapturesWithinGlobalTimeRange(product);
    const visibleCaptures = useGetCapturesWithinViewport(featureList);
    const activeCapture = useSelector(selectActiveCapture);
    const singleCaptureInView = useGetSingleCaptureWithinViewport(visibleCaptures);

    const [floatingSummaryCapture, setFloatingSummaryCapture] = useState<CapturePlusCount | null>(null);

    const { totalKgHr: floatingSummaryCaptureTotalKgHr } = useMemo(() => {
        return getStacCaptureEmissions(floatingSummaryCapture?.properties as CaptureFeatureProperties);
    }, [floatingSummaryCapture]);

    const selectorDates = useGetHistoricalSamplesForTarget(
        floatingSummaryCapture?.properties.target_group_id || floatingSummaryCapture?.properties.target_id
    );

    // Update `floatingSummaryCapture` to be the active target (if selected through temporal navigation)
    // or the only visible target. If no target is active and there are none or multiple in view, reset
    // the active capture and hide the floating summary.
    useEffect(() => {
        if (isFetching) {
            setFloatingSummaryCapture(null);
            return;
        }
        let capture = null;

        // Check if a capture has been activated through temporal navigation
        if (activeCapture && visibleCaptures.length) {
            const activeCaptureInView = visibleCaptures.find((c) => c.id === activeCapture.itemId);
            if (activeCaptureInView) capture = activeCaptureInView;
            else {
                dispatch(clearActiveCapture());
            }
        }
        // Otherwise, use the singular capture in the viewport if present
        else if (singleCaptureInView) capture = singleCaptureInView;

        setFloatingSummaryCapture(capture);
    }, [activeCapture, visibleCaptures, dispatch, isFetching, singleCaptureInView, drawerCardData]);

    const trackDayClick = (day: StacDateBlock, timestampString: string) => {
        analytics.clickFloatingSummaryObservation({
            itemId: day.id,
            platform,
            emissionRate: day.totalKgHr ?? NaN,
            label: timestampString
        });
    };

    const floatingTargetSummaryDates =
        floatingSummaryCapture &&
        selectorDates &&
        stacDatesToTimeSelector({
            dates: selectorDates,
            handleClick: (day: StacDateBlock) => {
                const targetId = floatingSummaryCapture.properties.target_id;
                const targetGroupId = floatingSummaryCapture.properties.target_group_id;
                handleTimeSelectorDayClick({ day, dispatch, targetId, targetGroupId, trackDayClick });
            }
        });

    /**
     * Closes a side drawer.
     */
    const closeDrawer = useCloseDrawer({
        drawerDispatch,
        dispatch
    });

    /**
     * Opens a side drawer (e.g. for customizing map settings).
     */
    const openDrawer = useOpenDrawer({
        drawerDispatch,
        dispatch,
        setActiveCardIndex,
        analytics
    });

    // set the snapPoint for the bottom-up drawer
    const setHeightBottomDrawer = useSetHeightBottomDrawer({
        drawerDispatch
    });

    const drawerHasMapData = useMemo(() => {
        return drawerState.emissionsDetails || drawerState.ogiData || drawerState.targetData;
    }, [drawerState]);

    const theme = useTheme();
    const colorScheme = theme.palette.mode;
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
    //determines if base map toggle and related buttons need to be repositioned to accommodate mobile drawer
    const isMobileDrawerPreview =
        isMobileScreen && drawerHasMapData && drawerState.bottomDrawerSnapPoint === SnapPointIds.preview;

    /**
     * Un-highlight OGI feature and close the drawer if:
     * highlighted object has the same id but different properties (changed cluster truthyness or count)
     * highlighted object is now null or undefined
     */
    const highlightedLayerId = useSelector(selectHighlightedLayerId);
    const zoom = useSelector(selectFlooredZoom);
    const shouldKeepDrawerOpenWithoutHighlight =
        fetchingDrawerInfo ||
        (drawerState.emissionsDetails &&
            !!drawerCardData?.length &&
            (drawerCardData as EmissionDrawerCardData[]).some((card) => card.dataUnavailable));
    useShouldCloseDrawers({
        drawerState,
        highlightedFeature,
        highlightedLayerId,
        zoom,
        globalDate,
        platform,
        product,
        closeDrawer,
        keepOpenWithoutHighlight: shouldKeepDrawerOpenWithoutHighlight
    });

    /** Opens the full map legend. */
    const openLegend = (source: 'mini legend' | 'speed dial' | 'header' | 'on-load') => {
        openDrawer('legend');
        return analytics.openLegend({ source });
    };

    const requestedTargetIds = useMemo(
        () => getArrayURLParams(emissionsDrawerSelection?.targets),
        [emissionsDrawerSelection]
    );

    /**
     * This is the function that triggers all date change actions -- INITIATED BY THE DATA DRAWERS --
     * in the app. This updates emissionDrawerCardData and fetchingDrawerInfo in redux,
     * which in turn will trigger an update to {@link refreshedLayerInfos}
     */
    const handleEmissionDrawerDateChange = useCallback(
        (day: StacDateBlock) => {
            if (!requestedTargetIds.length) return;
            const targetId = day.feature.properties?.target_id;
            const targetGroupId = day.feature.properties?.target_group_id;
            pendingDateChangeTargetId.current = targetGroupId || targetId;
            pendingDateChangeDayDate.current = day.date;

            const nextDrawerSelection = buildEmissionDrawerDateChangeState({
                emissionsDrawerSelection,
                day,
                targetId,
                targetGroupId,
                requestedTargetIds
            });

            dispatch(setFetchingDrawerInfo(true));
            dispatch(setEmissionsDrawerCardData(nextDrawerSelection));
            dispatch(setTargetDate({ date: getDayEnd(day.date).getTime(), targetId: targetGroupId || targetId }));
        },
        [dispatch, emissionsDrawerSelection, requestedTargetIds]
    );

    /**
     * Handles user click/touch on an object on the map:
     *   - empties drawer data
     *   - determines which type of drawer to open
     *   - opens drawer
     *   - adds map highlight
     */
    const handleMapInfo = useCallback(
        (
            info: PickInfoToPrioritize[] | undefined,
            clickedNoData: boolean,
            showNoDataDrawer: boolean,
            addMultipleHighlights?: boolean
        ) => {
            if (!isMapInteractive) {
                return;
            }
            setDrawerCardData(null);

            if (clickedNoData || !info) {
                dispatch(removeAllMapFeatureHighlights());
                if (showNoDataDrawer) {
                    openDrawer('noData');
                } else {
                    closeDrawer();
                }

                return;
            }

            const topInfo = info[0];
            if (addMultipleHighlights === true && info.every(isOGIInfo)) {
                dispatch(addOneMapFeatureHighlight(createHighlightPayload(topInfo)));
                // typescript doesn't understand the info.every check has already cleared
                // all items in the list, so this keeps it happy
                setDrawerCardData(info.filter((i) => isOGIInfo(i)) as OGIInfo[]);
                openDrawer('ogiData');
            } else {
                // If the top priority info is not a point/dispersed source, add one highlight and open `LayerDataDrawer`
                if (!isRasterInfo(topInfo) && !isPlumeEmissionInfo(topInfo)) {
                    dispatch(removeAllMapFeatureHighlights());
                    if (isTargetInfo(topInfo)) {
                        setDrawerCardData([topInfo] as TargetInfo[]);
                        openDrawer('targetData');
                    } else if (
                        isClusteredPointInfrastructureInfo(topInfo) ||
                        isPipelineInfo(topInfo) ||
                        isPointInfrastructureInfo(topInfo) ||
                        isBasinInfo(topInfo)
                    ) {
                        setDrawerCardData([topInfo] as OGIInfo[]);
                        openDrawer('ogiData');
                    }
                    const highlight = createHighlightPayload(topInfo);
                    dispatch(addOneMapFeatureHighlight(highlight));
                }
                // Otherwise, add one highlight for emission details and open `DrawerWithCards` with all infos
                else {
                    const highlight = createHighlightPayload(topInfo);
                    dispatch(addOneMapFeatureHighlight(highlight));
                    const cardData = emissionsInfoToCardProps(info);
                    setDrawerCardData(cardData);
                    const singleCardLayerId = checkForSingleCard(cardData);
                    setExpandedAccordion(singleCardLayerId);
                    openDrawer('emissionsDetails');
                }
            }
        },
        [dispatch, isMapInteractive, openDrawer, closeDrawer]
    );

    /**
     * Initial data fetch path. It runs once to build drawer/map state from Redux or URL selection
     * when the page loads or rehydrates.
     */
    const onceOnLoad = useRef(true);
    const initialLayerInfos = useSelectedFeaturePickInfos(onceOnLoad.current);

    /**
     * This is the drawer date-change refresh path. The call to `useSelectedFeaturePickInfos`
     * is only triggered when `fetchingDrawerInfo` is true.
     *
     * `allowEmptyResults=true` lets the page distinguish between:
     *   * the initial call on load (see {@link initialLayerInfos})
     *   * this call, which is triggered after {@link handleEmissionDrawerDateChange}
     */
    const refreshedLayerInfos = useSelectedFeaturePickInfos(fetchingDrawerInfo, true);

    useEffect(() => {
        if (isInitialized && initialLayerInfos && !!visibleCaptures && onceOnLoad.current) {
            // sends info to drawers
            handleMapInfo(initialLayerInfos, false, false);
            //
            // sends info to redux
            const { featureCoordinates, featureDate, featureTargetId, featureItemId } = getEmissionSelectionParams(
                initialLayerInfos[0]
            );
            const featureUrlDateString = formatDateForUrl(new Date(featureDate));

            // TODO: This can be deprecated eventually
            // https://methanesat.atlassian.net/browse/DP-5699
            dispatch(
                setSelectedFeatureParams({
                    coordinates: featureCoordinates,
                    targetDate: featureUrlDateString,
                    targetId: featureTargetId,
                    itemId: featureItemId
                })
            );
            dispatch(setActiveCapture(getActiveCaptureParams(initialLayerInfos[0])));
            onceOnLoad.current = false;
        }
    }, [dispatch, handleMapInfo, initialLayerInfos, isInitialized, visibleCaptures]);

    // Rebuild the existing emissions drawer in place after a card date change, including
    // unavailable cards, active card selection, and map highlight state.
    useEffect(() => {
        if (!fetchingDrawerInfo || refreshedLayerInfos === null) return;

        const targetDates = Object.fromEntries(
            requestedTargetIds.map((targetId) => {
                // Use the exact capture datetime for the clicked target so isSameTime can match
                // its individual date in the time selector tooltip.
                if (targetId === pendingDateChangeTargetId.current && pendingDateChangeDayDate.current) {
                    return [targetId, pendingDateChangeDayDate.current.toISOString()];
                }
                // For other unselected targets, use their "YYYY-MM-DD" date as stored in redux
                const requestedDate = emissionsDrawerSelection[`target-${targetId}-date`];
                return [targetId, requestedDate];
            })
        );

        const cards = fillUnavailableEmissionCards({
            cards: emissionsInfoToCardProps(refreshedLayerInfos),
            targetIds: requestedTargetIds,
            targetDates,
            emissionsDrawerSelection,
            fallbackDate: formatDateForUrl(new Date(globalDate))
        });

        setDrawerCardData(cards);

        const nextActiveCardIndex = getNextActiveEmissionCardIndex(cards, pendingDateChangeTargetId.current);
        const nextActiveCard = cards[nextActiveCardIndex];
        setActiveCardIndex(nextActiveCardIndex);

        setExpandedAccordion(null);

        dispatch(removeAllMapFeatureHighlights());

        /**
         * Keep the active capture selected after a time selector date change,
         * even when the refreshed card has no raster info at the selected point.
         */
        const highlightInfo = nextActiveCard.dispersedSourceEmissions[0];
        if (highlightInfo) {
            dispatch(addOneMapFeatureHighlight(createHighlightPayload(highlightInfo)));
            dispatch(setActiveCapture(getActiveCaptureParams(highlightInfo)));
        } else {
            if (typeof nextActiveCard.itemId === 'string') {
                dispatch(setActiveCapture({ itemId: nextActiveCard.itemId, targetId: nextActiveCard.targetId }));
            } else {
                dispatch(clearActiveCapture());
            }
        }

        dispatch(setFetchingDrawerInfo(false));
        pendingDateChangeTargetId.current = null;
        pendingDateChangeDayDate.current = null;
    }, [dispatch, emissionsDrawerSelection, fetchingDrawerInfo, globalDate, refreshedLayerInfos, requestedTargetIds]);

    /**
     * Handles when a user changes the type of the base map.
     */
    const handleMapToggleButtonClick = () => {
        const next = mapName === GoogleBasemapNames.Roadmap ? GoogleBasemapNames.Satellite : GoogleBasemapNames.Roadmap;

        dispatch(setEmissionsMapName(next));
        analytics.changeBasemap({ mapType: next });
    };

    const floatingSummaryCloseProps = useMemo(() => {
        return {
            showCloseButton: visibleCaptures.length > 1,
            onClose: () => {
                dispatch(clearActiveCapture());
                setFloatingSummaryCapture(null);
            }
        };
    }, [dispatch, visibleCaptures.length]);

    const useOnMapClick = useMemo(() => makeUseOnMapClick(handleMapInfo, -drawerWidth), [handleMapInfo, drawerWidth]);

    return (
        <PageContainer>
            <Head title={`${t('emissionsMapPage.title')} - MethaneSAT`} />
            <Header
                onOpenLegend={() => drawerDispatch({ type: 'open', id: 'legend' })}
                isLegendOpen={drawerState.legend}
                helpButtonRef={helpButtonRef}
                page={PageIds.emissionsMap}
            />
            {/** Dismiss-able banner for mobile screens*/}
            {SHOW_VIEW_ON_DESKTOP_ALERT ? (
                <Snackbar
                    open={isBannerOpen}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    sx={{
                        top: '60px',
                        display: {
                            sm: 'none',
                            xs: 'flex'
                        }
                    }}
                >
                    <Alert
                        data-testid="view-on-desktop-banner"
                        variant="filled"
                        severity="warning"
                        action={
                            <Button
                                data-testid="view-on-desktop-banner-dismiss-button"
                                color="inherit"
                                size="small"
                                onClick={handleBannerClose}
                            >
                                {t('header.dismiss')}
                            </Button>
                        }
                        sx={{
                            width: '100%'
                        }}
                    >
                        {t('header.viewOnDesktop')}
                    </Alert>
                </Snackbar>
            ) : null}
            <Box
                component="main"
                sx={{
                    position: 'relative',
                    flex: 1
                }}
            >
                <ErrorBoundary
                    onError={handleErrorBoundaryError('EmissionsPageMap')}
                    fallbackRender={() => {
                        return (
                            <Grid
                                container
                                direction="column"
                                sx={{ height: '100%', background: 'rgba(0,0,0,0.2)', padding: 5 }}
                                justifyContent="center"
                                alignItems="center"
                                spacing={2}
                            >
                                <Grid size="auto">
                                    <Typography component="p" variant="h1" sx={{ margin: 0 }}>
                                        Sorry! There was a problem loading the map. Please refresh the page and try
                                        again.
                                    </Typography>
                                </Grid>
                                <Grid size="auto">
                                    <Button
                                        data-testid="map-error-refresh"
                                        href="/en/emissions-map"
                                        variant="contained"
                                    >
                                        Refresh
                                    </Button>
                                </Grid>
                            </Grid>
                        );
                    }}
                >
                    <EmissionsPageMap data-testid="emissions-page-map" useOnMapClick={useOnMapClick} />
                </ErrorBoundary>
                <Box
                    sx={(theme) => ({
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        paddingTop: theme.spacing(1),
                        paddingX: theme.spacing(2),
                        // Specifying none here means that none of the children of this element
                        // will receive on click events. This allows us to to click through to
                        // the map
                        pointerEvents: 'none',
                        width: '100%'
                    })}
                >
                    {isTooltipVisible && <IntroTooltip />}
                    <MiniLegend
                        onOpenLegend={() => openLegend('mini legend')}
                        rightWidthOffset={allDrawersClosed ? 0 : -drawerWidth}
                    />
                </Box>
                <EmissionsPageMapDrawers
                    drawerState={drawerState}
                    drawerCardData={drawerCardData}
                    closeDrawer={closeDrawer}
                    setDrawerWidth={setDrawerWidth}
                    setHeightBottomDrawer={setHeightBottomDrawer}
                    activeCardIndex={activeCardIndex}
                    expandedAccordion={expandedAccordion}
                    handleAccordionChange={handleAccordionChange}
                    handleMapInfo={handleMapInfo}
                    handleEmissionDrawerDateChange={handleEmissionDrawerDateChange}
                    page={PageIds.emissionsMap}
                />

                {allDrawersClosed &&
                    floatingTargetSummaryDates &&
                    (areaEmissionLayerEnabled || plumeEmissionLayerEnabled) && (
                        <FloatingTargetSummary
                            title={t(`emissionsMapPage.mapModals.target.floatingTargetSummaryTitle.${platform}`)}
                            targetOrGroupId={
                                floatingSummaryCapture.properties.target_group_id ||
                                floatingSummaryCapture.properties.target_id
                            }
                            data={floatingTargetSummaryDates}
                            emissions={
                                floatingSummaryCaptureTotalKgHr
                                    ? t('emissionsMapPage.mapModals.target.emissionsPerHour', {
                                          kgHr: formatEmissions(floatingSummaryCaptureTotalKgHr)
                                      })
                                    : t('emissionsMapPage.mapModals.target.pendingData.total')
                            }
                            emissionsTitle={t('emissionsMapPage.mapModals.target.totalEmissionsTitle')}
                            overTimeTitle={t('emissionsMapPage.mapModals.common.overTimeTitle')}
                            closeProps={floatingSummaryCloseProps}
                            selectedValue={floatingSummaryCapture.properties.datetime}
                        />
                    )}
                {/* zoom controls */}
                {isMapInteractive && (
                    <Grid
                        data-testid="floating-container"
                        container
                        flexDirection="row"
                        alignItems="center"
                        position="absolute"
                        //move the section up the same amount as the bottom drawer preview height
                        bottom={isMobileDrawerPreview ? MOBILE_DRAWER_PREVIEW_HEIGHT : 0}
                        rowSpacing={2}
                        columnSpacing={2}
                        sx={(theme) => ({
                            paddingBottom: theme.spacing(4),
                            paddingX: theme.spacing(2),
                            // Specifying none here means that none of the children of this element
                            // will receive on click events. This allows us to to click through to
                            // the map
                            pointerEvents: 'none',
                            /** Horizontally scroll elements.
                             * pointerEvents is auto for the elements that are not grids.
                             * This allows the user to click through the row containing the dates.
                             */
                            '& :not(.MuiGrid2-root)': { pointerEvents: 'auto' },
                            //smooth the height change for when bottom mobile drawer is open/closed in preview
                            transition: isMobileDrawerPreview ? 'bottom .15s' : 'bottom .2s ease-in-out'
                        })}
                    >
                        <Grid size="auto">
                            <EmissionsPageZoomControls zoom={zoom} minZoom={minZoomBaseMap} maxZoom={maxZoomBaseMap} />
                        </Grid>
                        <Grid
                            size="auto"
                            sx={(theme) => ({
                                maxWidth: {
                                    xs: '100%',
                                    // small breakpoint (600px) minus the left padding
                                    // of this grid item's container (16px)
                                    sm: `calc(600px - ${theme.spacing(4)}) !important`,
                                    // more magic numbers: max width of the target summary panel,
                                    // the width of the zoom buttons, and the padding between and around
                                    // these elements
                                    md: `calc(100vw - 500px - 34px - ${theme.spacing(8)})`,
                                    lg: '700px !important'
                                }
                            })}
                        >
                            <MapToggleButton
                                data-testid="map-toggle-button"
                                onClick={handleMapToggleButtonClick}
                                imageSrc={GoogleBasemaps[mapName].img[colorScheme]}
                                TooltipProps={{
                                    title: <Typography variant="body1">{GoogleBasemaps[mapName].title}</Typography>
                                }}
                            />
                        </Grid>
                        {!(ONE_MSAT_DATE_RANGE && platform === Platforms.MSAT) && (
                            <Grid
                                size="auto"
                                sx={(theme) => ({
                                    maxWidth: {
                                        xs: '100%',
                                        // small breakpoint (600px) minus the left padding
                                        // of this grid item's container (16px)
                                        sm: `calc(600px - ${theme.spacing(4)}) !important`,
                                        // more magic numbers: max width of the target summary panel,
                                        // the width of the zoom buttons, and the padding between and around
                                        // these elements
                                        md: `calc(100vw - 500px - 34px - ${theme.spacing(8)})`,
                                        lg: '700px !important'
                                    }
                                })}
                            >
                                <DatePicker
                                    data-testid="global-date-picker"
                                    // sending down the platform value as a key so that TimeSelector refs properly update
                                    platform={platform}
                                    onCalendarIconClick={() => {
                                        // Close all mini legend chips on mobile click
                                        dispatch(toggleAllChips(false));

                                        //Close the bottom up drawer from preview on mobile
                                        closeDrawer();
                                    }}
                                    onClick={(date: number) => {
                                        analytics.chooseGlobalDate({
                                            label:
                                                // Use year for MAIR payloads so we can better understand what campaigns users are interacting with
                                                platform === Platforms.MSAT
                                                    ? `${date}`
                                                    : `${new Date(date).getFullYear()}`
                                        });
                                        // If a feature has been clicked on and its drawer is open, close it when the date changes from the picker
                                        if (highlightedFeature) {
                                            closeDrawer();
                                        }

                                        dispatch(setGlobalDate(date));
                                    }}
                                    onChange={(ev) => {
                                        analytics.chooseGlobalDate({
                                            label:
                                                platform === Platforms.MSAT
                                                    ? `${ev.target.value}`
                                                    : `${new Date(ev.target.value as number).getFullYear()}`
                                        });
                                        dispatch(setGlobalDate(ev.target.value as number));
                                    }}
                                    options={GLOBAL_DATE_OPTIONS}
                                    promptText={t('emissionsMapPage.datePicker.prompt')}
                                    value={globalDate}
                                    variant={getTimeSelectorTimespan(platform)}
                                    collapseDatePicker={isAnyChipExpanded || isMobileDrawerPreview}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Box>
        </PageContainer>
    );
}

/**
 * Used by Next.js at build time.
 * See https://nextjs.org/docs/pages/building-your-application/data-fetching/get-static-props
 */
export async function getStaticProps(
    context: GetStaticPropsContext
): Promise<GetStaticPropsResult<SupportedLocaleObject>> {
    const props = await addLanguageAndDictionaryToProps(context.params?.language, {}, { page: 'emissionsMap' });

    checkEmissionsMapEnv();

    return {
        props
    };
}

/**
 * Used by Next.js at build time.
 * See https://nextjs.org/docs/pages/building-your-application/data-fetching/get-static-paths
 */
export async function getStaticPaths(): Promise<GetStaticPathsResult> {
    return {
        paths: addLanguageToPathParams(),
        fallback: false
    };
}

export default EmissionsMap;
